import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { SessionTabs } from '../components/SessionTabs.js';
import { toast } from 'sonner';
import { api } from '../lib/api.js';
import { CanonicalCandidateDiff } from '../components/CanonicalCandidateDiff.js';

interface ReviewRow {
  id: string;
  rowNumber: number;
  matchTier: string;
  reason: string;
  status: string;
  decisionKind: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  createdAt: string;
  canonicalRecord: Record<string, unknown>;
  candidateRecord: Record<string, unknown> | null;
}

type Decision = 'link_to_candidate' | 'insert_as_new' | 'reject' | 'file_to_patient';

const isDocumentRow = (r: ReviewRow): boolean => (r.canonicalRecord as { kind?: string } | null)?.kind === 'document';

export function ReviewQueuePage({ sessionId }: { sessionId: string }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['review-queue', sessionId],
    queryFn: () => api.listReviewQueue(sessionId),
    refetchInterval: 3000,
  });
  const [expanded, setExpanded] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());

  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: Decision }) =>
      api.decideReviewItem(id, { decision }),
    onSuccess: () => {
      toast.success('Decision queued');
      void qc.invalidateQueries({ queryKey: ['review-queue', sessionId] });
      void qc.invalidateQueries({ queryKey: ['stats', sessionId] });
    },
    onError: (e) => toast.error(`Decision failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const bulk = useMutation({
    // Bulk decide is patient-only (documents are filed individually via a patient pick).
    mutationFn: (decision: 'link_to_candidate' | 'insert_as_new' | 'reject') =>
      api.bulkDecideReviewItems({ itemIds: [...selected], decision }),
    onSuccess: (res) => {
      toast.success(`${res.count} decisions queued`);
      setSelected(new Set());
      void qc.invalidateQueries({ queryKey: ['review-queue', sessionId] });
      void qc.invalidateQueries({ queryKey: ['stats', sessionId] });
    },
    onError: (e) => toast.error(`Bulk decide failed: ${e instanceof Error ? e.message : String(e)}`),
  });

  const rows = (data?.rows ?? []) as ReviewRow[];
  const pending = rows.filter((r) => r.status === 'pending');
  const resolved = rows.filter((r) => r.status !== 'pending');

  const allPendingSelected = useMemo(
    () => pending.length > 0 && pending.every((r) => selected.has(r.id)),
    [pending, selected],
  );

  const selectedRows = pending.filter((r) => selected.has(r.id));
  const linkDisabled = selectedRows.length === 0 || selectedRows.some((r) => !r.candidateRecord);

  function toggle(id: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAll() {
    setSelected((prev) => {
      if (pending.length > 0 && pending.every((r) => prev.has(r.id))) return new Set();
      return new Set(pending.map((r) => r.id));
    });
  }

  return (
    <div className="space-y-4 max-w-5xl mx-auto p-6">
      <SessionTabs sessionId={sessionId} active="review" />
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold" style={{ color: 'var(--text-primary)' }}>Review queue</h1>
          <div className="text-[12px]" style={{ color: 'var(--text-dimmed)' }}>
            {pending.length} pending · {resolved.length} resolved
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-[12px]" style={{ color: 'var(--text-dimmed)' }}>Loading…</div>
      ) : pending.length === 0 ? (
        <div className="rounded-md p-4 text-[12px]" style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', color: 'var(--text-dimmed)' }}>
          No pending reviews.
        </div>
      ) : (
        <>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-md text-[11px]" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
            <input
              type="checkbox"
              data-testid="review-select-all"
              checked={allPendingSelected}
              onChange={toggleAll}
              aria-label="Select all pending"
            />
            <span style={{ color: 'var(--text-dimmed)' }}>
              Select all pending ({pending.length})
            </span>
          </div>

          <div className="space-y-2">
            {pending.map((row) => {
              const isOpen = expanded === row.id;
              const isSelected = selected.has(row.id);
              return (
                <div key={row.id} className="rounded-md overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                  <div className="flex items-center" style={{ background: isOpen ? 'var(--bg-tertiary)' : 'var(--bg-secondary)' }}>
                    <label className="px-3 py-2 cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`review-row-checkbox-${row.id}`}
                        checked={isSelected}
                        onChange={() => toggle(row.id)}
                        aria-label={`Select row ${row.rowNumber}`}
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setExpanded(isOpen ? null : row.id)}
                      className="flex-1 text-left px-2 py-2 flex items-center justify-between"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded" style={{ background: 'rgba(250,204,21,0.18)', color: 'var(--accent-yellow)' }}>
                          {row.matchTier}
                        </span>
                        <span className="text-[12px] truncate" style={{ color: 'var(--text-primary)' }}>row {row.rowNumber} — {row.reason}</span>
                      </div>
                      <span className="text-[10px]" style={{ color: 'var(--text-dimmed)' }}>{isOpen ? '▾' : '▸'}</span>
                    </button>
                  </div>
                  {isOpen && (
                    <div className="p-3 space-y-3" style={{ background: 'var(--bg-secondary)', borderTop: '1px solid var(--border)' }}>
                      {isDocumentRow(row) ? (
                        <DocumentReviewControl
                          sessionId={sessionId}
                          row={row}
                          onDecided={() => {
                            void qc.invalidateQueries({ queryKey: ['review-queue', sessionId] });
                            void qc.invalidateQueries({ queryKey: ['stats', sessionId] });
                          }}
                        />
                      ) : (
                        <>
                          <CanonicalCandidateDiff canonical={row.canonicalRecord} candidate={row.candidateRecord} />
                          <div className="flex flex-wrap gap-2">
                            {row.candidateRecord && (
                              <button
                                type="button"
                                onClick={() => decide.mutate({ id: row.id, decision: 'link_to_candidate' })}
                                disabled={decide.isPending}
                                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
                              >
                                Link to candidate
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => decide.mutate({ id: row.id, decision: 'insert_as_new' })}
                              disabled={decide.isPending}
                              className="rounded-lg px-3 py-1.5 text-xs font-semibold"
                              style={{ background: 'rgba(148,163,184,0.18)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
                            >
                              Mark as new
                            </button>
                            <button
                              type="button"
                              onClick={() => decide.mutate({ id: row.id, decision: 'reject' })}
                              disabled={decide.isPending}
                              className="rounded-lg px-3 py-1.5 text-xs font-semibold"
                              style={{ background: 'rgba(238,93,80,0.18)', color: 'var(--accent-red)', border: '1px solid rgba(238,93,80,0.3)' }}
                            >
                              Reject
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {selected.size > 0 && (
            <div
              data-testid="review-bulk-toolbar"
              className="sticky bottom-3 mt-3 rounded-lg px-3 py-2 flex items-center gap-3 flex-wrap"
              style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border)', boxShadow: '0 4px 12px rgba(0,0,0,0.25)' }}
            >
              <span className="text-[12px]" style={{ color: 'var(--text-primary)' }}>
                {selected.size} selected
              </span>
              <button
                type="button"
                onClick={() => bulk.mutate('link_to_candidate')}
                disabled={bulk.isPending || linkDisabled}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
                style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
              >
                Link {selected.size} selected
              </button>
              <button
                type="button"
                onClick={() => bulk.mutate('insert_as_new')}
                disabled={bulk.isPending}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ background: 'rgba(148,163,184,0.18)', color: 'var(--text-primary)', border: '1px solid var(--border)' }}
              >
                Mark {selected.size} as new
              </button>
              <button
                type="button"
                onClick={() => bulk.mutate('reject')}
                disabled={bulk.isPending}
                className="rounded-lg px-3 py-1.5 text-xs font-semibold"
                style={{ background: 'rgba(238,93,80,0.18)', color: 'var(--accent-red)', border: '1px solid rgba(238,93,80,0.3)' }}
              >
                Reject {selected.size} selected
              </button>
              <button
                type="button"
                onClick={() => setSelected(new Set())}
                disabled={bulk.isPending}
                className="ml-auto text-[11px] underline"
                style={{ color: 'var(--text-dimmed)' }}
              >
                Clear
              </button>
            </div>
          )}
        </>
      )}

      {resolved.length > 0 && (
        <div className="space-y-1 pt-4">
          <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: 'var(--text-dimmed)' }}>Resolved ({resolved.length})</div>
          <div className="space-y-1">
            {resolved.slice(0, 50).map((row) => (
              <div key={row.id} className="rounded-md px-3 py-1.5 text-[11px] flex items-center justify-between" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}>
                <span style={{ color: 'var(--text-primary)' }}>row {row.rowNumber} — {row.decisionKind ?? row.status}</span>
                <span className="font-mono" style={{ color: 'var(--text-dimmed)' }}>{row.resolvedBy ?? ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * WS2.5 — resolve an unmatched document by filing it to a hand-picked patient.
 * Lists the session's imported patients (from the FHIR cache) with a name/MRN/DOB
 * filter; "File to patient" stages a DocumentReference + triggers the load so the
 * binary lands in Cerbo (documents + pt_documents). "Reject" leaves it unfiled.
 */
function DocumentReviewControl({ sessionId, row, onDecided }: { sessionId: string; row: ReviewRow; onDecided: () => void }) {
  const [filter, setFilter] = useState('');
  const [patientId, setPatientId] = useState('');
  const { data } = useQuery({
    queryKey: ['session-patients', sessionId],
    queryFn: () => api.getSessionPatients(sessionId),
  });
  const patients = useMemo(
    () =>
      (data?.patients ?? []).map((p) => ({
        fhirId: p.patientId,
        label: p.name || p.patientId,
        mrn: p.mrn,
        dob: p.dob,
        search: `${p.name} ${p.mrn} ${p.dob}`.toLowerCase(),
      })),
    [data],
  );
  const filtered = useMemo(() => {
    const f = filter.trim().toLowerCase();
    return (f ? patients.filter((p) => p.search.includes(f)) : patients).slice(0, 100);
  }, [patients, filter]);
  const file = useMutation({
    mutationFn: () => api.decideReviewItem(row.id, { decision: 'file_to_patient', patientId }),
    onSuccess: () => { toast.success('Document filed — loading to Cerbo'); onDecided(); },
    onError: (e) => toast.error(`File failed: ${e instanceof Error ? e.message : String(e)}`),
  });
  const reject = useMutation({
    mutationFn: () => api.decideReviewItem(row.id, { decision: 'reject' }),
    onSuccess: () => { toast.success('Document rejected'); onDecided(); },
    onError: (e) => toast.error(`Reject failed: ${e instanceof Error ? e.message : String(e)}`),
  });
  const rec = row.canonicalRecord as { filename?: string; file?: string };
  const busy = file.isPending || reject.isPending;
  const inputStyle = { background: 'var(--bg-primary)', border: '1px solid var(--border)', color: 'var(--text-primary)' } as const;
  return (
    <div className="space-y-2">
      <div className="text-[12px]" style={{ color: 'var(--text-primary)' }}>
        Unmatched document: <span className="font-mono">{rec.filename ?? rec.file ?? row.id}</span>
      </div>
      <div className="text-[11px]" style={{ color: 'var(--text-dimmed)' }}>Pick the patient this document belongs to ({patients.length} imported):</div>
      <input
        type="text"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Filter by name / MRN / DOB…"
        className="w-full rounded-lg px-3 py-1.5 text-sm outline-none"
        style={inputStyle}
      />
      <select
        value={patientId}
        onChange={(e) => setPatientId(e.target.value)}
        size={6}
        className="w-full rounded-lg px-2 py-1 text-sm outline-none"
        style={inputStyle}
      >
        {filtered.map((p) => (
          <option key={p.fhirId} value={p.fhirId}>
            {p.label}{p.mrn ? ` · ${p.mrn}` : ''}{p.dob ? ` · ${p.dob}` : ''}
          </option>
        ))}
      </select>
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => file.mutate()}
          disabled={!patientId || busy}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-40"
          style={{ background: 'linear-gradient(135deg, #0dcfb8 0%, #06b6d4 100%)' }}
        >
          File to patient
        </button>
        <button
          type="button"
          onClick={() => reject.mutate()}
          disabled={busy}
          className="rounded-lg px-3 py-1.5 text-xs font-semibold"
          style={{ background: 'rgba(238,93,80,0.18)', color: 'var(--accent-red)', border: '1px solid rgba(238,93,80,0.3)' }}
        >
          Reject
        </button>
      </div>
    </div>
  );
}
